/**
 * TOSS Web Application — сетки карточек: колонок столько, сколько влезает, но карточка не уже минимума
 *
 * Один источник классов для ВСЕХ сеток карточек продукта — и для самих сеток, и для их
 * скелетонов. Пока каждая страница писала свою прогрессию (`grid-cols-1 md:grid-cols-2
 * xl:grid-cols-3`), две вещи ломались постоянно.
 *
 * **Первое: брейкпоинт вьюпорта не знает, сколько места у сетки на самом деле.** Ширину
 * карточки задаёт не окно, а КОНТЕЙНЕР: панель фильтров рядом, боковая панель заявки, колонка
 * дашборда. На окне 1440 сетка кабинета получала три колонки и при открытой панели, и без неё —
 * и в первом случае карточка сжималась до ~270 px, где «ст. Москва-Товарная, путь 3» ломается
 * на две строки, а «Рефрижераторные контейнеры, −18 °C» — на три. Владелец 28.08.2026: «такого
 * не должно быть, должно быть минимальное значение, дальше которого она не может уменьшаться».
 *
 * **Второе: скелетон и сетка расходились.** Число колонок повторялось в `PageSkeletonState
 * gridCols` строкой, скопированной руками, — и при любой правке сетки лента прыгала в момент,
 * когда приходили данные. Здесь у обоих один импорт.
 *
 * Форма решения — та же, что в соседнем продукте (`padel_app`, `components/filters/catalog-grids.ts`):
 * `repeat(auto-fill, minmax(min(100%, N), 1fr))`.
 *
 * - `auto-fill` — колонок столько, сколько поместится по ширине контейнера. Ступеней нет:
 *   карточка перетекает 4→3→2→1 вслед за реальным местом, а не за размером окна.
 * - `min(100%, N)` — минимум отключается, когда контейнер УЖЕ минимума: без него на телефоне
 *   (375 px) трек шириной 24rem распёр бы страницу горизонтальной прокруткой.
 * - `auto-fill`, а НЕ `auto-fit`: пустые треки остаются пустыми. На «Последних заявках» с одной
 *   заявкой `auto-fit` растянул бы её карточку во всю ширину дашборда — строка фактов длиной
 *   в экран читается хуже, чем карточка нормального размера и воздух справа.
 *
 * **Минимум — это ширина, ниже которой карточка начинает врать о содержимом**, а не ширина
 * «покрасивее». Меняешь минимум — проверь на стенде обе поверхности с ОТКРЫТОЙ панелью
 * фильтров: именно там карточка самая узкая.
 */

/**
 * Карточка заявки (`ApplicationSummaryCard` на обеих поверхностях): 20rem.
 *
 * `rem`, а не `px`, потому что корневой кегль в этом продукте плавающий — 15.4 / 16.5 / 17.6 px
 * по ширине окна (`index.css`), — и минимум обязан ехать вместе с текстом: на десктопе 20rem это
 * 352 px, на телефоне 308. Складывается он из того, что карточка обязана уместить в одну строку:
 * боковые отступы 32 + подпись факта 72 + зазор 12 + значение ≈ 240 px, а 240 — это ширина самой
 * длинной типовой строки продукта («Рефрижераторные контейнеры, −18 °C»).
 *
 * Замерено на стенде 28.08.2026 (кабинет, панель фильтров открыта): окно 1024 → колонка 376 и
 * одна карточка, 1440 → две по 388, 1920 → три по 362. Прежняя прогрессия по вьюпорту давала на
 * 1440 три колонки по 253 px — те самые, из-за которых значения ломались надвое.
 *
 * Минимум подобран так, чтобы «мёртвой» ширины не было. Карточка полезна в двух видах: узкая
 * (≈360–420, факты одной колонкой) и широкая (≥760, факты в две — их порог живёт в самой
 * карточке и тоже считается от содержимого). Между ними — вид, где факты уже одноколоночные, а
 * места ещё вдвое больше нужного, и правая половина карточки пустует. На перечисленных выше
 * ширинах сетка в эту зону не попадает; сдвинешь минимум вверх на пару rem — попадёт.
 */
export const APPLICATION_CARD_GRID_COLS =
  'grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))]';

/** Готовая сетка карточек заявок с обычным для продукта зазором. */
export const APPLICATION_CARD_GRID = `grid gap-4 ${APPLICATION_CARD_GRID_COLS}`;

/**
 * Карточка-строка: документ, шаблон, уведомление — заголовок, подпись под ним и действие
 * справа. 26rem — на десктопе это ~458 px.
 *
 * Больше, чем у заявки, не по недосмотру: тут нет фактов, которые можно перенести на вторую
 * строку, зато есть `truncate` — на узкой карточке имя документа не ломается, а МОЛЧА
 * обрезается, и две карточки подряд превращаются в «Отчёт по заявке РЖДЛ-2026…» и «Отчёт по
 * заявке РЖДЛ-2026…». Минимум держит ту ширину, на которой номер заявки виден целиком.
 */
export const ROW_CARD_GRID_COLS = 'grid-cols-[repeat(auto-fill,minmax(min(100%,26rem),1fr))]';
